/**
 * NACHWEISZEILEN JE PAUSCHALMONAT — reine Ableitung, kein Schreibpfad.
 *
 * Im Pauschalprojekt bucht der Versand eines Begehungsberichts eine
 * Nachweiszeile auf die Pauschale (NW-…, Menge 0, flatRateLogic:
 * planFlatProofEntry). Das Geld zaehlt weiter allein ueber die Monatsbuchungen
 * (FLAT-JJJJ-MM). Dieses Modul beantwortet die Frage, die Rechnung und
 * Leistungsnachweis daneben stellen: welche Begehungen stecken in welchem
 * Monat? (Buchhaltung 5633, 30.09.2026: „Juli und August mit je 2
 * Begehungen".)
 *
 * Blattnah wie flatRateLogic: nur Typ-Importe und die beiden Blattmodule
 * serviceLogUtils/flatRateLogic — Rechnungsstrang und PDF-Generator lesen es
 * beide, ohne einen Importzyklus zu bauen.
 */

import type { ServiceTransaction } from '../types';
import {
    buchungstag,
    isBackfilledAltEntry,
    isFlatProofEntry,
    isMainInspectionBooking,
    isPostingEntry,
    kennungFuerVergleich,
    reportNumberOf,
} from './serviceLogUtils';
import {
    type FlatCoverageServiceLike,
    flatKeyLabel,
    flatMonthKey,
    isFlatBookingReference,
    isInspectionProofEntry,
    isReportProofEntry,
    resolveFlatCoverage,
} from './flatRateLogic';

/** Ein Monat der Pauschale mit den Nachweiszeilen, die in ihm liegen. */
export interface FlatMonthProof {
    /** Monatsschluessel, 'FLAT-2026-07'. */
    key: string;
    /** Anzeige, '07/2026'. */
    label: string;
    /**
     * Gibt es fuer diesen Monat eine Monatsbuchung (FLAT-Schluessel mit
     * Menge > 0)? Die Verschlusszeile der Umstellung (Menge 0) ist KEINE: sie
     * verschliesst einen Monat, der je Einzelposition abgerechnet wurde.
     */
    monthBooked: boolean;
    /**
     * Begehungen des Monats: Nachweiszeilen, die als Begehung zaehlen
     * (isInspectionProofEntry), UND Berichtsbuchungen auf Positionen, die
     * genau diese Pauschale deckt (die Monate vor der Umstellung auf
     * Nachweiszeilen) — derselbe Bericht einmal je Monat.
     */
    inspections: number;
    /** Alle Nachweiszeilen des Monats, zeitlich geordnet (ohne die Buchungen auf gedeckten Positionen). */
    entries: ServiceTransaction[];
}

/** Kind-Ref im Protokoll der Mutter — die Form von serviceLogUtils.childBookingRef. */
const KIND_REF = /^RPT-CHILD-([0-9a-fA-F-]{36})-(\d+)$/;
/** Kind-Hauptbuchung — die Form von serviceLogUtils.childBookingDescription. */
const KIND_TEXT = /^Tochter-Projekt "(.*)": Bericht Nr\. (\d+)/;

/**
 * Welcher Bericht steckt hinter der Zeile — 'eigen:N' oder 'kind:…:N' —,
 * oder null.
 *
 * Ohne Abschnitt: die Berichtsnummer ist projektweit (reportBooking,
 * isReportBooked), und derselbe Bericht kann mit verschiedenen Abschnitten im
 * Protokoll stehen. Eigen und Tochter muessen getrennt bleiben: Bericht 3 der
 * Tochter ist nicht Bericht 3 der Mutter. Die Tochter-Altform ohne Ref kennt
 * nur den Namen — sie steht aus der Zeit vor dem Ref und damit nie im selben
 * Monat wie eine Nachweiszeile desselben Berichts (die traegt immer den Ref).
 */
const reportKey = (entry: ServiceTransaction): string | null => {
    const kindRef = KIND_REF.exec(String(entry.reportRef ?? ''));
    if (kindRef) return `kind:${kindRef[1].toLowerCase()}:${Number(kindRef[2])}`;
    const kindText = KIND_TEXT.exec(String(entry.description ?? ''));
    if (kindText) return `kind-name:${kindText[1]}:${Number(kindText[2])}`;
    const nummer = reportNumberOf(entry);
    return nummer === null ? null : `eigen:${nummer}`;
};

/**
 * Hauptbuchung eines Berichts auf einer GEDECKTEN Position — so standen die
 * Begehungen eines Pauschalprojekts vor den Nachweiszeilen im Protokoll
 * (26041002: Juni bis August als +1 auf 2.7.10).
 *
 * Dieselbe Aussage wie coversInspection (serviceJournal.inferInspectionCoverage)
 * fuer eine Mengenzeile: Arbeit (keine Verbuchung, keine Alt-Rechnung), Menge
 * > 0, keine Zusatzleistung, und ein Berichtsbezug — kanonische Hauptbuchung,
 * 'RPT-…'-Ref oder Tochter-Hauptbuchung. Ohne Bezug keine Begehung: eine
 * Handbuchung auf einer gedeckten Position kann auch eine Baubesprechung sein.
 */
const isCoveredReportBooking = (entry: ServiceTransaction): boolean => {
    if (!((entry.quantity || 0) > 0)) return false;
    if (isPostingEntry(entry) || isBackfilledAltEntry(entry)) return false;
    const description = String(entry.description ?? '');
    if (description.startsWith('Zusatzleistung')) return false;
    return (
        isMainInspectionBooking(entry) ||
        String(entry.reportRef ?? '').startsWith('RPT-') ||
        KIND_TEXT.test(description)
    );
};

/**
 * Monat einer Nachweiszeile: 'FLAT-JJJJ-MM' des BERLINER Kalendertags, oder
 * null ohne lesbares Datum.
 *
 * Nicht der UTC-Tag des Zeitstempels: eine Begehung am 01.08. um 00:30 Uhr
 * steht als '2026-07-31T22:30Z' im Protokoll und gehoert trotzdem in den
 * August — derselbe Tag, den Tagesriegel und Journal rechnen (buchungstag).
 */
export const proofMonthKey = (date?: string | null): string | null => flatMonthKey(buchungstag(date));

/**
 * Die Nachweiszeilen einer Pauschale, je Monat zusammengefasst.
 *
 * Ein Monat erscheint, sobald er eine Monatsbuchung ODER eine Nachweiszeile
 * hat — ein gebuchter Monat ganz ohne Begehung ist ein regulaerer Fall (die
 * Pauschale kauft Bereitschaft), eine Begehung im noch ungebuchten laufenden
 * Monat ebenso.
 *
 * fromDay/toDay (Tagesangaben, einschliesslich) begrenzen die Nachweiszeilen
 * auf den Tag genau und die Monatsbuchungen auf ihren Monat: eine
 * Monatsbuchung gehoert ueber ihren Schluessel zu einem Monat, nicht ueber
 * ihr Datumsfeld (das trug frueher das Buchungsdatum, 5633 am 07.09.).
 *
 * Dieselbe Nachweis-Referenz zaehlt nur einmal: sie ist aus dem Bericht
 * abgeleitet (flatProofReference), eine zweite Zeile damit ist eine Dublette
 * desselben Berichts und keine zweite Begehung.
 *
 * BESTANDSMONATE: bis zur Umstellung auf Nachweiszeilen buchte der Versand
 * eine Menge auf die gedeckte Begehungsposition (2.7.10), nicht auf die
 * Pauschale. Ohne `services` stuende fuer jeden dieser Monate „keine
 * Begehung" auf dem Kundenbeleg — das Gegenteil der Wahrheit. Deshalb zaehlen
 * die Berichtsbuchungen auf Positionen, die GENAU diese Pauschale deckt
 * (resolveFlatCoverage), mit: je Bericht und Monat einmal, auch wenn er auf
 * 2.7.10 UND als Nachweis steht, und auf einer Stundenposition (2.7.40, 3,5 h)
 * als eine Begehung, nicht als Stundenzahl. Ohne Berichtsnummer zaehlt der
 * Berliner Tag als ein Bericht. Pflichtparameter, damit kein Aufrufer die
 * Bestandsmonate still vergisst; null heisst ausdruecklich „nur Nachweise".
 */
export const flatMonthProofSummary = (
    serviceLog: ServiceTransaction[] | null | undefined,
    flat: FlatCoverageServiceLike | null | undefined,
    services: FlatCoverageServiceLike[] | null | undefined,
    fromDay?: string | null,
    toDay?: string | null,
): FlatMonthProof[] => {
    if (!flat?.id) return [];
    const from = buchungstag(fromDay);
    const to = buchungstag(toDay);
    const fromKey = flatMonthKey(from);
    const toKey = flatMonthKey(to);
    const coverage = resolveFlatCoverage(services);

    const months = new Map<string, FlatMonthProof>();
    const month = (key: string): FlatMonthProof => {
        let m = months.get(key);
        if (!m) {
            m = { key, label: flatKeyLabel(key) ?? key, monthBooked: false, inspections: 0, entries: [] };
            months.set(key, m);
        }
        return m;
    };

    // Berichte je Monat, die schon als Begehung gezaehlt sind.
    const countedReports = new Map<string, Set<string>>();
    const countInspection = (m: FlatMonthProof, report: string | null): void => {
        if (report) {
            let counted = countedReports.get(m.key);
            if (!counted) {
                counted = new Set();
                countedReports.set(m.key, counted);
            }
            if (counted.has(report)) return;
            counted.add(report);
        }
        m.inspections += 1;
    };

    const seenRefs = new Set<string>();
    for (const entry of Array.isArray(serviceLog) ? serviceLog : []) {
        if (!entry) continue;

        if (entry.serviceId !== flat.id) {
            if (coverage.get(entry.serviceId) !== flat.id || !isCoveredReportBooking(entry)) continue;
            const day = buchungstag(entry.date);
            const key = flatMonthKey(day);
            if (!day || !key) continue;
            if ((from && day < from) || (to && day > to)) continue;
            countInspection(month(key), reportKey(entry) ?? `tag:${day}`);
            continue;
        }

        const ref = String(entry.bookingReference ?? '').trim();

        if (isFlatBookingReference(ref)) {
            if (!((entry.quantity || 0) > 0)) continue;
            if ((fromKey && ref < fromKey) || (toKey && ref > toKey)) continue;
            month(ref).monthBooked = true;
            continue;
        }

        if (!isFlatProofEntry(entry)) continue;
        const day = buchungstag(entry.date);
        const key = flatMonthKey(day);
        if (!day || !key) continue;
        if ((from && day < from) || (to && day > to)) continue;
        if (seenRefs.has(ref)) continue;
        seenRefs.add(ref);

        const m = month(key);
        m.entries.push(entry);
        if (isInspectionProofEntry(entry, flat)) {
            // Hand-Begehungen ohne Bericht (NW-BEG-…) haben keinen Bericht, mit
            // dem sie zusammenfallen koennten — die Referenz oben haelt sie einmalig.
            countInspection(m, isReportProofEntry(entry) ? reportKey(entry) : null);
        }
    }

    const out = [...months.values()].sort((a, b) => a.key.localeCompare(b.key));
    out.forEach((m) => m.entries.sort((a, b) => String(a.date ?? '').localeCompare(String(b.date ?? ''))));
    return out;
};

const inspectionsText = (n: number): string =>
    n === 0 ? 'keine Begehung' : n === 1 ? '1 Begehung' : `${n} Begehungen`;

/**
 * Beschreibung einer Pauschalzeile auf der Rechnung:
 * '07/2026: 2 Begehungen · 08/2026: 2 Begehungen'.
 *
 * Genau die uebergebenen Monate, zeitlich geordnet und je einmal — der
 * Aufrufer entscheidet, welche Monate die Zeile abrechnet. Ein Monat ohne
 * Nachweis heisst ausdruecklich 'keine Begehung': er wird trotzdem gefordert
 * (Bereitschaft), und ein fehlender Monat in der Aufzaehlung laese sich wie
 * ein Rechenfehler. Unlesbare Schluessel fallen weg; ohne Monat ''.
 */
export const flatLineDescription = (
    summary: FlatMonthProof[] | null | undefined,
    monthKeys: Array<string | null | undefined> | null | undefined,
): string => {
    const byKey = new Map((summary ?? []).map((m) => [m.key, m]));
    const keys = [...new Set((monthKeys ?? []).map((k) => String(k ?? '').trim()))]
        .filter((k) => isFlatBookingReference(k))
        .sort();
    return keys
        .map((key) => `${flatKeyLabel(key)}: ${inspectionsText(byKey.get(key)?.inspections ?? 0)}`)
        .join(' · ');
};

/**
 * Die Monate, die eine Pauschalzeile abrechnet — aus ihrer Zuordnung
 * (InvoiceLine.allocations bzw. deren Vorhersage).
 *
 * Nur eine VOLLSTAENDIGE Antwort zaehlt: fuehrt eine Zuordnung auf einen
 * Eintrag ohne Monatsschluessel, oder deckt die Zuordnung die Menge nicht
 * (undatierte, direkt am Leistungsstand korrigierte Menge), kommt null — die
 * Zeile nennte sonst weniger Monate, als sie fordert, und das stuende
 * woertlich auf dem Beleg. Lieber keine Beschreibung als eine falsche.
 */
export const flatLineMonthKeys = (
    allocations: ReadonlyArray<{ entryId?: string | null; quantity?: number | null }> | null | undefined,
    serviceLog: ServiceTransaction[] | null | undefined,
    expectedQty: number,
): string[] | null => {
    const list = allocations ?? [];
    if (list.length === 0) return null;
    // Beide Seiten ueber `kennungFuerVergleich` (dieselbe Regel wie
    // allocationLogic.allocatedByEntry): die Zuordnung nennt eine uuid so, wie
    // sie beim Ausstellen hiess, das Journal gibt sie kleingeschrieben zurueck.
    const refById = new Map<string, string>();
    for (const entry of Array.isArray(serviceLog) ? serviceLog : []) {
        if (entry?.id) refById.set(kennungFuerVergleich(entry.id), String(entry.bookingReference ?? '').trim());
    }
    const keys = new Set<string>();
    let sum = 0;
    for (const allocation of list) {
        const ref = refById.get(kennungFuerVergleich(allocation?.entryId));
        if (!ref || !isFlatBookingReference(ref)) return null;
        keys.add(ref);
        sum += allocation?.quantity || 0;
    }
    if (Math.abs(sum - (expectedQty || 0)) > 0.0005) return null;
    return [...keys].sort();
};
